
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Header from './Header';
import BottomNav from './BottomNav';
import RecordForm from '../records/RecordForm';
import { X, Loader } from 'lucide-react';
import LoadingScreen from './LoadingScreen';
import { db } from '../../firebase';
import { doc, updateDoc, getDocs, collection, query, where, limit } from 'firebase/firestore';
import { AnimatePresence, motion } from 'framer-motion';
import { useTutorial } from '../../contexts/TutorialContext';
import TutorialController from '../tutorial/TutorialController';
import LevelUpOverlay from '../common/LevelUpOverlay';

// Lazy load components
const AllRecords = lazy(() => import('../records/AllRecords'));
const RankingTab = lazy(() => import('../ranking/RankingTab'));
const MyPageTab = lazy(() => import('../user/MyPageTab'));
const ClassBattleTab = lazy(() => import('../room/ClassBattleTab'));
const ResultPage = lazy(() => import('../records/ResultPage'));
const RoomList = lazy(() => import('../room/RoomList'));
const StudyRoom = lazy(() => import('../room/StudyRoom'));
const UserDetailView = lazy(() => import('../user/UserDetailView'));
const StudyTab = lazy(() => import('../study/StudyTab'));
const SubmissionsTab = lazy(() => import('../records/SubmissionsTab'));
const TimetableTab = lazy(() => import('../calendar/TimetableTab'));
const UpdateNotes = lazy(() => import('../settings/UpdateNotes'));
const UpcomingEventsTab = lazy(() => import('../user/UpcomingEventsTab'));
const UserListTab = lazy(() => import('../user/UserListTab'));
const CommunityGoalsPage = lazy(() => import('../../pages/CommunityGoalsPage'));
const TimelineComparator = lazy(() => import('../user/TimelineComparator')); 
const NotificationsPage = lazy(() => import('../../pages/NotificationsPage'));
const SettingsTab = lazy(() => import('../settings/SettingsTab'));

const mainTabs = {
  '/records': MyPageTab,
  '/ranking': RankingTab,
  '/all-records': AllRecords,
  '/classbattle': ClassBattleTab,
  '/study-room': RoomList,
  '/result': ResultPage,
  '/study': StudyTab,
  '/submissions': SubmissionsTab,
  '/timetable': TimetableTab,
  '/updatenotes': UpdateNotes,
  '/upcoming-events': UpcomingEventsTab,
  '/user-list': UserListTab,
  '/goals-community': CommunityGoalsPage,
  '/timeline': TimelineComparator, 
  '/notifications': NotificationsPage,
};

// 洗練されたスケルトン風フォールバック
const SuspenseFallback = () => (
  <div className="w-full max-w-2xl mx-auto p-4 space-y-6 pt-24 animate-pulse">
    <div className="h-8 bg-slate-200 dark:bg-slate-800 rounded-lg w-1/3" />
    <div className="grid grid-cols-2 gap-4">
      <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded-3xl" />
      <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded-3xl" />
    </div>
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-48 bg-slate-200 dark:bg-slate-800 rounded-3xl" />
      ))}
    </div>
  </div>
);

const MainLayout = ({
  user,
  onLogout,
  onSave,
  onLinkWithGoogle,
  onLinkEmailPassword,
  error,
  showCountdown,
  onToggleCountdown,
  onToggleTheme,
  countdownHeight,
  setCountdownHeight,
}) => {
  const [isRecordModalOpen, setRecordModalOpen] = useState(false);
  const [levelUpData, setLevelUpData] = useState(null); // { addedMinutes, previousTotalMinutes, newBadges }
  const navigate = useNavigate();
  const location = useLocation();
  const isDarkMode = user?.theme === 'dark';
  const { startTutorial } = useTutorial();

  useEffect(() => {
    const checkAndStartTutorial = async () => {
      if (!user || user.isAnonymous) return; 
      if (user.tutorial_completed) return;

      const recordsQuery = query(
        collection(db, 'records'),
        where('userId', '==', user.uid),
        limit(1)
      );
      const querySnapshot = await getDocs(recordsQuery);

      if (querySnapshot.empty) {
        const timer = setTimeout(() => {
          startTutorial();
        }, 1000); 
        return () => clearTimeout(timer);
      }
    };

    checkAndStartTutorial();
  }, [user, startTutorial]);

  const handleNavigateToProfile = () => {
    navigate('/records');
  };

  const handleRecordComplete = (data) => {
    setRecordModalOpen(false);
    // data: { addedMinutes, oldTotal, newBadges } passed from RecordForm
    if (data && data.addedMinutes > 0) {
      setLevelUpData({
        addedMinutes: data.addedMinutes,
        previousTotalMinutes: data.oldTotal,
        newBadges: data.newBadges || []
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background-dark">
      <Header
        user={user}
        onLogout={onLogout}
        onSave={onSave}
        onLinkWithGoogle={onLinkWithGoogle}
        onLinkEmailPassword={onLinkEmailPassword}
        showCountdown={showCountdown}
        onToggleCountdown={onToggleCountdown}
        countdownHeight={countdownHeight}
        setCountdownHeight={setCountdownHeight}
        onStreakClick={handleNavigateToProfile}
        isDarkMode={isDarkMode}
        toggleDarkMode={onToggleTheme}
      />
      {user?.isAnonymous && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 mt-16 mb-0 mx-auto max-w-3xl rounded-md shadow-md text-sm dark:bg-yellow-900/50 dark:border-yellow-600 dark:text-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold">Googleアカウントと連携しませんか？</p>
              <p className="text-xs">学習記録を安全に保存し、全ての機能を利用できるようになります。</p>
            </div>
            <button
              onClick={onLinkWithGoogle}
              className="ml-4 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-semibold text-xs shadow-md transition duration-150 ease-in-out whitespace-nowrap transform hover:scale-105"
            >
              Google連携
            </button>
          </div>
          {error && error.includes("Googleアカウント連携") && <p role="alert" className="text-red-600 text-xs mt-2">{error}</p>}
        </div>
      )}
      <main className={`flex-1 pb-16 ${user?.isAnonymous ? 'pt-2' : 'pt-0'}`}>
        <Suspense fallback={<SuspenseFallback />}>
          <Routes>
            {Object.entries(mainTabs).map(([path, Component]) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
            <Route path="/settings" element={
                <SettingsTab 
                    user={user}
                    onLogout={onLogout}
                    onToggleTheme={onToggleTheme}
                    isDarkMode={isDarkMode}
                    showCountdown={showCountdown}
                    onToggleCountdown={onToggleCountdown}
                    onLinkWithGoogle={onLinkWithGoogle}
                    onLinkEmailPassword={onLinkEmailPassword}
                />
            } />
            <Route path="/study-room/:roomId" element={<StudyRoom />} />
            <Route path="/user-detail/:uid" element={<UserDetailView />} />
            <Route path="/" element={<Navigate to="/all-records" replace />} />
          </Routes>
        </Suspense>
      </main>

      <AnimatePresence>
        {isRecordModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 animate-fadeIn"
            onClick={() => setRecordModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg relative max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setRecordModalOpen(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white z-10 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="閉じる"
              >
                <X size={24} />
              </button>
              <RecordForm 
                onClose={() => setRecordModalOpen(false)} 
                isModalMode={true} 
                onRecordComplete={handleRecordComplete}
              />
            </div>
          </div>
        )}
      </AnimatePresence>
      
      {levelUpData && (
        <LevelUpOverlay 
          isOpen={!!levelUpData} 
          onClose={() => setLevelUpData(null)}
          addedMinutes={levelUpData.addedMinutes}
          previousTotalMinutes={levelUpData.previousTotalMinutes}
          newBadges={levelUpData.newBadges}
        />
      )}

      <TutorialController />

      <BottomNav onRecordButtonClick={() => setRecordModalOpen(true)} />
    </div>
  );
};

export default MainLayout;
